// @flow
import type {Node} from 'react'
import React, {useCallback, useMemo} from 'react'
import {compose, map, sortBy, prop, values} from 'ramda'
import Select from 'react-select'
import classNames from 'classnames'

import FormGroup from 'components/ui/FormGroup'
import type {Path, Option, UpdateCallback} from 'components/proposals/types'
import Value from 'components/proposals/Part/ui/Value'

type Props = {
  availableDomains: Object,
  changeKey: string,
  disabled: boolean,
  domainDatabaseId: Value,
  onChange: UpdateCallback,
  path: Path,
  strikethrough: boolean,
}

const makeOption = ({databaseId, description}) => ({value: databaseId, label: description})
const makeOptions = compose(map(makeOption), sortBy(prop('description')), values)

function SelectDomain({onChange, path, disabled, strikethrough, changeKey, availableDomains, domainDatabaseId}:
  Props): Node {
  const options = useMemo(() => makeOptions(availableDomains), [availableDomains])
  const selectedId = domainDatabaseId.isDeleted ? domainDatabaseId.prevValue : domainDatabaseId.nextValue
  const selectedOption = selectedId ? options.find((option) => option.value === selectedId) : null
  const id = `EditPolicy-${changeKey}-domain`

  const onSelect = useCallback((option: Option) => {
    const optionId = option.value
    domainDatabaseId.setValue(optionId)
    onChange(path, domainDatabaseId)
  }, [path, onChange, domainDatabaseId])

  const selectClassName = classNames('refinery-select', 'refinery-value-pill', {
    'refinery-select--strikethrough': strikethrough,
    'refinery-value-pill--unchanged': domainDatabaseId.isUnchanged,
    'refinery-value-pill--changed': !domainDatabaseId.isUnchanged,
  })

  return (
    <FormGroup
      label={I18n.t('proposals.policy_edit.affects_domain')}
      medium
      target={id}
    >
      <Select
        className={selectClassName}
        classNamePrefix="Select"
        isDisabled={disabled}
        isClearable
        inputId={id}
        onChange={onSelect}
        options={options}
        placeholder={I18n.t('policies.no_domain')}
        value={selectedOption}
      />
    </FormGroup>
  )
}

export default SelectDomain
